// TypeScript types for TUM Onboarding Module

export type OnboardingStage = 
  | 'initiated' 
  | 'data_import' 
  | 'validation' 
  | 'mapping' 
  | 'configuration' 
  | 'review' 
  | 'go_live' 
  | 'completed';

export type OnboardingStatus = 
  | 'draft' 
  | 'in_progress' 
  | 'pending_approval' 
  | 'approved' 
  | 'live' 
  | 'cancelled';

export type BatchType = 
  | 'properties' 
  | 'units' 
  | 'tenants' 
  | 'accounts' 
  | 'vendors' 
  | 'bill_sources' 
  | 'gl_codes';

export type BatchStatus = 
  | 'uploaded' 
  | 'validating' 
  | 'validated' 
  | 'mapping' 
  | 'importing' 
  | 'completed' 
  | 'failed';

export type ValidationStatus = 'pending' | 'valid' | 'invalid' | 'warning';

export type MatchStatus = 'no_match' | 'exact_match' | 'fuzzy_match' | 'conflict';

export type ImportStatus = 'pending' | 'imported' | 'skipped' | 'failed';

export type SignOffStatus = 'pending' | 'approved' | 'rejected' | 'skipped';

export type BillSourceType = 
  | 'email' 
  | 'api' 
  | 'portal_scraping' 
  | 'manual_upload' 
  | 'ftp' 
  | 'partner_feed';

export type CheckFrequency = 'real_time' | 'hourly' | 'daily' | 'weekly' | 'manual';

export type EntityType = 'property' | 'unit' | 'account' | 'tenant' | 'supplier';

// ============================================================================
// Onboarding Project
// ============================================================================
export interface OnboardingProject {
  id: string;
  workspace_id: string;
  project_name: string;
  customer_name: string;
  current_stage: OnboardingStage;
  overall_status: OnboardingStatus;
  completion_percentage: number;
  target_go_live_date: string | null;
  actual_go_live_date: string | null;
  project_owner_id: string | null;
  assigned_team: string[];
  customer_contact_email: string | null;
  customer_contact_name: string | null;
  stage_signoffs: Record<string, any>;
  estimated_properties: number;
  estimated_units: number;
  estimated_accounts: number;
  estimated_account_count?: number; // Alternative field name
  notes: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
  // Enhanced fields
  customer_logo_url?: string | null;
  logo_url?: string | null; // Alias for customer_logo_url
  is_archived?: boolean;
  archived_at?: string | null;
  archived_by?: string | null;
  spoc_name?: string | null; // Single Point of Contact name
  spoc_email?: string | null;
  spoc_phone?: string | null;
  spoc_role?: string | null;
}

export interface CreateProjectRequest {
  project_name: string;
  customer_name: string;
  customer_contact_name?: string;
  customer_contact_email?: string;
  target_go_live_date?: string;
  estimated_properties?: number;
  estimated_units?: number;
  estimated_accounts?: number;
  project_owner_id: string;
  assigned_team?: string[];
  notes?: string;
}

export interface UpdateProjectRequest {
  project_name?: string;
  customer_name?: string;
  current_stage?: OnboardingStage;
  overall_status?: OnboardingStatus;
  completion_percentage?: number;
  target_go_live_date?: string;
  customer_contact_name?: string;
  customer_contact_email?: string;
  assigned_team?: string[];
  notes?: string;
  estimated_properties?: number;
  estimated_units?: number;
  estimated_accounts?: number;
}

// ============================================================================
// Import Batches
// ============================================================================
export interface ImportBatch {
  id: string;
  project_id: string;
  batch_type: BatchType;
  filename: string;
  file_url: string;
  file_size_bytes: number | null;
  total_rows: number;
  status: BatchStatus;
  rows_processed: number;
  rows_success: number;
  rows_failed: number;
  rows_warning: number;
  validation_errors: ValidationError[];
  import_errors: ImportError[];
  warnings: ValidationWarning[];
  field_mapping: Record<string, string>;
  uploaded_by_user_id: string;
  processed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface UploadImportRequest {
  project_id: string;
  batch_type: BatchType;
  file: File;
}

export interface FieldMappingRequest {
  batch_id: string;
  field_mapping: Record<string, string>;
}

// ============================================================================
// Staged Data
// ============================================================================
export interface StagedData {
  id: string;
  batch_id: string;
  row_number: number;
  source_data: Record<string, any>;
  mapped_data: Record<string, any>;
  validation_status: ValidationStatus;
  validation_issues: ValidationIssue[];
  match_status: MatchStatus;
  matched_entity_id: string | null;
  match_confidence: number | null;
  import_status: ImportStatus;
  imported_entity_id: string | null;
  import_error: string | null;
  created_at: string;
}

export interface ValidationIssue {
  field: string;
  error_type: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationError {
  row_number: number;
  field: string;
  error_type: string;
  message: string;
}

export interface ValidationWarning {
  row_number: number;
  field: string;
  message: string;
}

export interface ImportError {
  row_number: number;
  entity_type: string;
  error: string;
}

// ============================================================================
// Validation Results
// ============================================================================
export interface ValidationResults {
  total_rows: number;
  valid_rows: number;
  invalid_rows: number;
  warning_rows: number;
  errors: ValidationError[];
  warnings: ValidationWarning[];
  duplicates: DuplicateMatch[];
}

export interface DuplicateMatch {
  row_number: number;
  matched_entity_id: string;
  match_type: 'exact' | 'fuzzy';
  confidence: number;
}

// ============================================================================
// Bill Sources
// ============================================================================
export interface BillSource {
  id: string;
  workspace_id: string;
  account_id: string;
  source_type: BillSourceType;
  is_active: boolean;
  priority: number;
  email_address: string | null;
  email_folder: string | null;
  email_subject_filter: string | null;
  api_endpoint: string | null;
  api_auth_type: string | null;
  api_credentials_encrypted: string | null;
  portal_url: string | null;
  portal_login_encrypted: string | null;
  ftp_host: string | null;
  ftp_path: string | null;
  ftp_credentials_encrypted: string | null;
  check_frequency: CheckFrequency;
  last_check_at: string | null;
  next_check_at: string | null;
  bills_retrieved: number;
  last_success_at: string | null;
  consecutive_failures: number;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateBillSourceRequest {
  account_ids: string[];
  source_type: BillSourceType;
  config: BillSourceConfig;
}

export interface BillSourceConfig {
  email_address?: string;
  email_folder?: string;
  email_subject_filter?: string;
  api_endpoint?: string;
  api_auth_type?: string;
  api_credentials?: Record<string, any>;
  portal_url?: string;
  portal_login?: Record<string, any>;
  ftp_host?: string;
  ftp_path?: string;
  ftp_credentials?: Record<string, any>;
  check_frequency?: CheckFrequency;
}

// ============================================================================
// GL Code Mappings
// ============================================================================
export interface GLCodeMapping {
  id: string;
  workspace_id: string;
  mapping_name: string;
  is_active: boolean;
  applies_to_utility_type: string | null;
  applies_to_property_id: string | null;
  applies_to_supplier_id: string | null;
  applies_to_tags: string[];
  gl_account_code: string;
  gl_cost_center: string | null;
  gl_department: string | null;
  gl_project_code: string | null;
  description: string | null;
  notes: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
}

export interface CreateGLCodeMappingRequest {
  mapping_name: string;
  applies_to_utility_type?: string;
  applies_to_property_ids?: string[];
  applies_to_supplier_ids?: string[];
  applies_to_tags?: string[];
  gl_account_code: string;
  gl_cost_center?: string;
  gl_department?: string;
  gl_project_code?: string;
  description?: string;
  notes?: string;
}

// ============================================================================
// Tags
// ============================================================================
export interface Tag {
  id: string;
  workspace_id: string;
  tag_name: string;
  tag_color: string;
  tag_category: string | null;
  description: string | null;
  created_at: string;
}

export interface TagAssignment {
  id: string;
  tag_id: string;
  entity_type: EntityType;
  entity_id: string;
  assigned_by_user_id: string;
  created_at: string;
}

// ============================================================================
// Stage Sign-offs
// ============================================================================
export interface StageSignOff {
  id: string;
  project_id: string;
  stage_name: string;
  status: SignOffStatus;
  signed_by_user_id: string | null;
  signed_at: string | null;
  comments: string | null;
  conditions_met: Record<string, boolean>;
  required_signoff: boolean;
  signoff_role: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubmitSignOffRequest {
  stage_name: string;
  status: 'approved' | 'rejected';
  comments?: string;
  conditions_met?: Record<string, boolean>;
}

// ============================================================================
// Insights
// ============================================================================
export interface OnboardingInsights {
  id: string;
  project_id: string;
  insight_date: string;
  total_properties: number;
  total_units: number;
  total_accounts: number;
  total_tenants: number;
  accounts_with_bill_source: number;
  accounts_with_gl_code: number;
  accounts_with_tags: number;
  accounts_missing_utility_type: number;
  data_completeness_score: number | null;
  readiness_score: number | null;
  critical_issues: number;
  warnings: number;
  details: Record<string, any>;
  created_at: string;
}

// ============================================================================
// Go-Live
// ============================================================================
export interface GoLiveRequest {
  go_live_date: string; // ISO datetime
  include_historical_from?: string; // ISO date
  notes?: string | null;
  notification_settings?: {
    notify_team: boolean;
    notify_customer: boolean;
    daily_summary_emails?: string[];
  };
}

// Pre-flight checklist item for go-live
export interface PreFlightItem {
  id: string;
  label: string;
  description: string;
  status: 'passed' | 'failed' | 'pending' | 'checking';
  required: boolean;
  details?: string;
}

// Activation step progress
export interface ActivationStep {
  id: string;
  name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  details?: string;
  entities_created?: number;
}

// Activation result from edge function
export interface ActivationResult {
  success: boolean;
  entities_created: {
    properties: number;
    units: number;
    accounts: number;
    tenants: number;
    bill_sources: number;
  };
  errors?: string[];
  hypercare_start: string;
  hypercare_end: string;
}

// Hypercare status
export interface HypercareStatus {
  start_date: string;
  end_date: string;
  days_remaining: number;
  is_active: boolean;
  progress_percentage: number;
}

// ============================================================================
// Stage Configuration
// ============================================================================
export interface StageConfig {
  number: number;
  name: string;
  path: string;
  description: string;
  required: boolean;
}

export const ONBOARDING_STAGES: StageConfig[] = [
  {
    number: 1,
    name: 'Project Initiation',
    path: 'stage-1',
    description: 'Set up the onboarding project with customer details and timeline',
    required: true,
  },
  {
    number: 2,
    name: 'Data Import',
    path: 'stage-2',
    description: 'Bulk import properties, units, tenants, accounts, and vendors via CSV/XLSX',
    required: true,
  },
  {
    number: 3,
    name: 'Validation & Matching',
    path: 'stage-3',
    description: 'Ensure data quality and completeness before configuration',
    required: true,
  },
  {
    number: 4,
    name: 'Mapping & Configuration',
    path: 'stage-4',
    description: 'Configure bill sources, GL codes, tags, and operational settings',
    required: true,
  },
  {
    number: 5,
    name: 'Review & Testing',
    path: 'stage-5',
    description: 'Dry run of bill processing with test data',
    required: true,
  },
  {
    number: 6,
    name: 'Customer Sign-Off',
    path: 'stage-6',
    description: 'Formal approval from customer before activation',
    required: true,
  },
  {
    number: 7,
    name: 'Go-Live',
    path: 'stage-7',
    description: 'Activate the customer and begin live bill processing',
    required: true,
  },
];

// ============================================================================
// Account Configuration (Stage 3)
// ============================================================================
export type CollectionSourceType = 'portal' | 'email' | 'manual';

export interface CollectionConfig {
  vendor_name?: string;
  portal_url?: string;
  username?: string;
  password?: string;
  email_integration_completed?: boolean;
  forwarding_email?: string;
}

export interface ParsingFieldSelection {
  field_id: string;
  field_name: string;
  field_type: string;
  is_selected: boolean;
  is_mandatory: boolean;
  is_required: boolean;
}

export interface AccountConfiguration {
  id: string;
  project_id: string;
  imported_account_id?: string;
  collection_source?: CollectionSourceType;
  collection_frequency?: string;
  collection_config: CollectionConfig;
  collection_completed: boolean;
  parsing_fields: ParsingFieldSelection[];
  parsing_config: Record<string, unknown>;
  parsing_completed: boolean;
  validation_config: Record<string, unknown>;
  gl_code_config: Record<string, unknown>;
  payment_config: Record<string, unknown>;
  approval_config: Record<string, unknown>;
  configuration_status: 'incomplete' | 'partial' | 'complete';
  apply_to_property: boolean;
  created_at: string;
  updated_at: string;
}

export interface ParsingField {
  id: string;
  workspace_id: string;
  field_name: string;
  field_type: string;
  is_default: boolean;
  is_mandatory: boolean;
  sort_order: number;
  created_at: string;
}

// ============================================================================
// Sign-Off Activity & Invitations (Phase 8)
// ============================================================================
export interface SignOffActivity {
  id: string;
  project_id: string;
  stage_name: string;
  action: 'created' | 'submitted' | 'approved' | 'rejected' | 'invitation_sent' | 'invitation_viewed';
  user_id?: string;
  user_name?: string;
  user_email?: string;
  timestamp: string;
  details?: Record<string, any>;
}

export interface CustomerSignOffInvitation {
  id: string;
  project_id: string;
  customer_email: string;
  customer_name?: string;
  personal_message?: string;
  invitation_token: string;
  status: 'pending' | 'sent' | 'viewed' | 'completed' | 'expired';
  sent_at?: string;
  expires_at: string;
  completed_at?: string;
  created_at: string;
}

export interface InviteCustomerRequest {
  customer_email: string;
  customer_name?: string;
  personal_message?: string;
  expires_days?: number;
}

